<script setup lang="ts">
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useCurrencyFormat } from '@/composables/useCurrencyFormat'
import type {
	ScheduledSwapResponse,
	Subscription,
	SubscriptionStatus,
} from '@/modules/plans/interfaces/subscription'

interface Props {
	open: boolean
	successSubscription: Subscription | null
	scheduledSwap: ScheduledSwapResponse | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
	(e: 'update:open', value: boolean): void
	(e: 'accept'): void
}>()

const statusConfig: Record<SubscriptionStatus, { label: string; cls: string; icon: string }> = {
	active: { label: 'Activo', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: 'check_circle' },
	trialing: { label: 'Prueba', cls: 'text-blue-700 bg-blue-50 border-blue-200', icon: 'labs' },
	past_due: { label: 'Vencido', cls: 'text-orange-700 bg-orange-50 border-orange-200', icon: 'warning' },
	paused: { label: 'Pausado', cls: 'text-gray-600 bg-gray-100 border-gray-200', icon: 'pause_circle' },
	cancelled: { label: 'Cancelado', cls: 'text-red-700 bg-red-50 border-red-200', icon: 'cancel' },
}

const fmtDate = (iso: string | null) =>
	iso
		? new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).format(
				new Date(iso)
			)
		: '—'

const daysBetween = (startIso: string, endIso: string) => {
	const start = new Date(startIso)
	const end = new Date(endIso)
	const diffMs = end.getTime() - start.getTime()
	return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
}
</script>

<template>
	<Dialog :open="props.open" @update:open="(v) => emit('update:open', v)">
		<DialogContent v-if="successSubscription || scheduledSwap" class="sm:max-w-md">
			<DialogHeader class="text-center">
				<div
					class="w-16 h-16 bg-[#253C70] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
				>
					<span class="material-symbols-outlined text-white text-[32px]">check</span>
				</div>
				<DialogTitle class="text-xl">
					<template v-if="scheduledSwap?.swapType === 'upgrade'">{{ scheduledSwap.message }}</template>
					<template v-else-if="scheduledSwap">Cambio programado</template>
					<template v-else>¡Suscripción activada!</template>
				</DialogTitle>
				<DialogDescription>
					<template v-if="scheduledSwap?.swapType === 'downgrade'">{{ scheduledSwap.message }}</template>
					<template v-else-if="scheduledSwap">El cargo prorrateado se aplicó a tu método de pago.</template>
					<template v-else>Tu plan ha sido procesado correctamente.</template>
				</DialogDescription>
			</DialogHeader>

			<!-- Scheduled swap details -->
			<div v-if="scheduledSwap" class="py-4 space-y-3">
				<div class="flex items-center justify-between py-2 border-b border-gray-100">
					<span class="text-sm text-gray-500">Tipo de cambio</span>
					<span class="text-sm font-semibold text-gray-900 capitalize">{{ scheduledSwap.swapType }}</span>
				</div>
				<div
					v-if="scheduledSwap.effectiveAt"
					class="flex items-center justify-between py-2 border-b border-gray-100"
				>
					<span class="text-sm text-gray-500">Fecha efectiva</span>
					<span class="text-sm font-semibold text-gray-900">{{ fmtDate(scheduledSwap.effectiveAt) }}</span>
				</div>
				<div
					v-if="scheduledSwap.proratedAmount"
					class="flex items-center justify-between py-2 border-b border-gray-100"
				>
					<span class="text-sm text-gray-500">Cargo prorrateado</span>
					<span class="text-sm font-semibold text-gray-900">
						{{ useCurrencyFormat(scheduledSwap.proratedAmount, 'MXN') }}
					</span>
				</div>
			</div>

			<!-- Plan details -->
			<div v-else-if="successSubscription" class="py-4 space-y-3">
				<div class="flex items-center justify-between py-2 border-b border-gray-100">
					<span class="text-sm text-gray-500">Plan</span>
					<span class="text-sm font-semibold text-gray-900">{{ successSubscription.planName }}</span>
				</div>
				<div class="flex items-center justify-between py-2 border-b border-gray-100">
					<span class="text-sm text-gray-500">Estado</span>
					<span
						class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border"
						:class="statusConfig[successSubscription.status]?.cls"
					>
						<span class="material-symbols-outlined text-[11px]">
							{{ statusConfig[successSubscription.status]?.icon }}
						</span>
						{{ statusConfig[successSubscription.status]?.label }}
					</span>
				</div>
				<div
					v-if="successSubscription.priceMxn > 0"
					class="flex items-center justify-between py-2 border-b border-gray-100"
				>
					<span class="text-sm text-gray-500">Precio</span>
					<span class="text-sm font-semibold text-gray-900">
						{{ useCurrencyFormat(successSubscription.priceMxn, 'MXN') }}
						/ {{ successSubscription.billingInterval === 'annual' ? 'año' : 'mes' }}
					</span>
				</div>
				<div
					v-if="successSubscription.trialEndsAt"
					class="flex items-center justify-between py-2 border-b border-gray-100"
				>
					<span class="text-sm text-gray-500">Trial hasta</span>
					<span class="text-sm font-semibold text-gray-900">
						{{ fmtDate(successSubscription.trialEndsAt) }}
					</span>
				</div>

				<!-- Periodo actual -->
				<div class="rounded-xl bg-gray-50 p-4 mt-2">
					<div class="flex items-center gap-2 mb-2">
						<span class="material-symbols-outlined text-gray-400 text-[18px]">calendar_month</span>
						<span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Período actual</span>
					</div>
					<div class="flex items-center justify-between">
						<div class="text-left">
							<p class="text-[11px] text-gray-400">Inicio</p>
							<p class="text-sm font-medium text-gray-700">
								{{ fmtDate(successSubscription.currentPeriodStart ?? successSubscription.createdAt) }}
							</p>
						</div>
						<div class="flex flex-col items-center px-3">
							<span class="text-lg font-bold text-[#253C70]">
								{{
									daysBetween(
										successSubscription.currentPeriodStart ?? successSubscription.createdAt,
										successSubscription.currentPeriodEnd
									)
								}}
							</span>
							<span class="text-[10px] text-gray-400 uppercase">días</span>
						</div>
						<div class="text-right">
							<p class="text-[11px] text-gray-400">Fin</p>
							<p class="text-sm font-medium text-gray-700">
								{{ fmtDate(successSubscription.currentPeriodEnd) }}
							</p>
						</div>
					</div>
				</div>
			</div>

			<DialogFooter>
				<button
					type="button"
					class="w-full px-4 py-2.5 text-sm font-semibold rounded-xl bg-[#253C70] text-white hover:bg-[#1d2f58] transition-colors"
					@click="$emit('accept')"
				>
					Aceptar
				</button>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>
